import { useState } from 'react';
import {
  Eye, EyeSlash, X, FloppyDisk, ShieldStar, ShieldCheck, Users as UsersIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';

const ROLE_META = {
  admin:   { labelKey: 'users.role.admin',   descKey: 'users.role.adminDesc',   Icon: ShieldStar,  badge: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  manager: { labelKey: 'users.role.manager', descKey: 'users.role.managerDesc', Icon: ShieldCheck, badge: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-500' },
  user:    { labelKey: 'users.role.user',    descKey: 'users.role.userDesc',    Icon: UsersIcon,   badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',      dot: 'bg-zinc-400' },
};

/**
 * User create/edit dialog — extracted from `UsersSettings.js` to keep the
 * parent page focused on list rendering + data-fetching. The parent owns the
 * `draft` state and passes it in; this component only handles form UI +
 * open/close wiring.
 *
 * Props:
 *   editing              — 'new' | user-id | null (null = closed)
 *   draft, setDraft      — controlled form state
 *   employees            — for the employee-link picker
 *   capKeys, roleCaps    — for the per-user capability-override matrix
 *   transCap(key, t)     — translator function for capability keys
 *   saving               — disable buttons while save is in flight
 *   onClose, onSave      — callbacks
 */
export default function UserFormDialog({
  editing, draft, setDraft, employees, mfgDepartments, capKeys, roleCaps,
  transCap, saving, onClose, onSave,
}) {
  const { t } = useT();
  const [showPw, setShowPw] = useState(false);
  const isNew = editing === 'new';

  return (
    <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? t('users.addUser') : t('users.editUser')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-700">{t('users.f.name')} <span className="text-red-500">*</span></Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('users.f.namePh')}
              data-testid="user-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-700">{t('users.f.email')} <span className="text-red-500">*</span></Label>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              placeholder="you@sensbey.co.jp"
              data-testid="user-email"
              disabled={!isNew}
            />
            {!isNew && <p className="text-[10px] text-zinc-400">{t('users.emailLock')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-700">
              {isNew ? <>{t('users.f.password')} <span className="text-red-500">*</span> {t('users.f.pwMin')}</> : t('users.f.newPw')}
            </Label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                placeholder={isNew ? '••••••••' : t('users.f.pwKeepPh')}
                className="pr-10"
                data-testid="user-password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700 rounded"
              >
                {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-700">{t('users.f.role')}</Label>
            <div className="grid grid-cols-1 gap-2">
              {['admin', 'manager', 'user'].map((role) => {
                const meta = ROLE_META[role];
                const Icon = meta.Icon;
                const selected = draft.role === role;
                return (
                  <button
                    type="button"
                    key={role}
                    onClick={() => setDraft({ ...draft, role })}
                    data-testid={`user-role-${role}`}
                    className={`text-left flex items-start gap-3 px-3 py-2.5 rounded-lg border-2 transition-all ${
                      selected
                        ? 'border-teal-700 bg-teal-700/5'
                        : 'border-zinc-200 hover:border-zinc-300 bg-white'
                    }`}
                  >
                    <span className={`flex items-center justify-center w-8 h-8 rounded-md ${selected ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600'} shrink-0`}>
                      <Icon size={16} weight={selected ? 'fill' : 'duotone'} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold ${selected ? 'text-teal-700' : 'text-zinc-900'}`}>{t(meta.labelKey)}</p>
                      <p className="text-[11px] text-zinc-500 leading-tight mt-0.5">{t(meta.descKey)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-700">{t('users.f.employee')}</Label>
            <Select
              value={draft.employee_id || '__none'}
              onValueChange={(v) => setDraft({ ...draft, employee_id: v === '__none' ? '' : v })}
            >
              <SelectTrigger data-testid="user-employee"><SelectValue placeholder={t('users.f.employeePh')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">{t('users.f.noLink')}</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} {e.code ? `(${e.code})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-zinc-400">{t('users.linkHint')}</p>
          </div>
          {/* A8 · 製造部スコープで使う所属製造部門（任意） */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-700">{t('users.f.mfgDept')}</Label>
            <Select
              value={draft.mfg_department_id || '__none'}
              onValueChange={(v) => setDraft({ ...draft, mfg_department_id: v === '__none' ? '' : v })}
            >
              <SelectTrigger data-testid="user-mfg-dept"><SelectValue placeholder={t('users.f.noLink')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">{t('users.f.noLink')}</SelectItem>
                {(mfgDepartments || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-zinc-400">{t('users.f.mfgDeptHint')}</p>
          </div>
          {(capKeys || []).length > 0 && (
            <div className="space-y-2 rounded-lg border border-zinc-200 p-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-700">{t('users.capTitle')}</Label>
                <p className="text-[10px] text-zinc-400 mt-0.5">
                  {draft.role === 'admin' ? t('users.capHintAdmin') : t('users.capHint', { role: t(ROLE_META[draft.role]?.labelKey) })}
                </p>
              </div>
              <div className="space-y-1.5" data-testid="user-capability-overrides">
                {capKeys.map((key) => {
                  const inherited = !!(roleCaps?.[draft.role]?.[key]);
                  const ov = draft.capability_overrides || {};
                  const current = key in ov ? (ov[key] ? 'allow' : 'deny') : 'inherit';
                  const setState = (state) => {
                    const next = { ...ov };
                    if (state === 'inherit') delete next[key];
                    else next[key] = state === 'allow';
                    setDraft({ ...draft, capability_overrides: next });
                  };
                  const opts = [
                    { v: 'inherit', label: t('users.cap.inherit', { state: inherited ? t('users.cap.allow') : t('users.cap.deny') }) },
                    { v: 'allow', label: t('users.cap.allow') },
                    { v: 'deny', label: t('users.cap.deny') },
                  ];
                  return (
                    <div key={key} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-zinc-700 truncate">{transCap(key, t)}</span>
                      <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden shrink-0" data-testid={`cap-override-${key}`}>
                        {opts.map((o) => {
                          const active = current === o.v;
                          const tone = o.v === 'allow' ? 'bg-emerald-500 text-white'
                            : o.v === 'deny' ? 'bg-red-500 text-white'
                            : 'bg-zinc-700 text-white';
                          return (
                            <button
                              key={o.v}
                              type="button"
                              onClick={() => setState(o.v)}
                              data-testid={`cap-override-${key}-${o.v}`}
                              className={`px-2 py-1 text-[10px] font-medium transition-colors ${
                                active ? tone : 'bg-white text-zinc-500 hover:bg-zinc-50'
                              }`}
                            >
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!isNew && (
            <div className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2.5 bg-zinc-50/50">
              <div>
                <p className="text-xs font-semibold text-zinc-700">{t('users.activeToggle')}</p>
                <p className="text-[11px] text-zinc-500">{t('users.activeToggleHint')}</p>
              </div>
              <Switch
                checked={draft.is_active}
                onCheckedChange={(v) => setDraft({ ...draft, is_active: v })}
                data-testid="user-active"
              />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} className="w-full sm:w-auto">
            <X size={14} className="mr-1" /> {t('common.cancel')}
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="user-save" className="bg-teal-700 hover:bg-teal-800 w-full sm:w-auto">
            <FloppyDisk size={14} className="mr-1" /> {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
